import { canEditStation } from '@app/core/stations/helpers';
import { Station } from '@app/core/types/stations';
import { useQueryParam } from '@mi-front/core';
import { LinkItem } from '@mi-front/ui';
import { defineMessage, useIntl } from 'react-intl';

export enum TabKey {
  INFO = 'info',
  GROUPS_PERMISSIONS = 'groups-permissions',
  USERS_PERMISSIONS = 'users-permissions',
}

const tabMessages = {
  info: defineMessage({
    id: 'admin.stations.viewScreen.basicInfo',
    defaultMessage: 'Info',
  }),
  groupsPermissions: defineMessage({
    id: 'admin.stations.viewScreen.groupsPermissions',
    defaultMessage: 'Permisos de grupos',
  }),
  usersPermissions: defineMessage({
    id: 'admin.stations.viewScreen.usersPermissions',
    defaultMessage: 'Permisos de usuarios',
  }),
};

/**
 * Builds header nav tabs for the station view and resolves the active tab from `?tab=`.
 * Permission tabs are only included when the user can edit the station.
 */
export function useAdminStationsViewTabs(station: Station): {
  tabs: LinkItem[];
  activeTab: TabKey;
} {
  const { $t } = useIntl();
  const canEdit = canEditStation(station);
  const tabParam = useQueryParam('tab');
  const basePath = `/admin/stations/${station.slug}`;

  const availableTabs = canEdit
    ? [TabKey.INFO, TabKey.GROUPS_PERMISSIONS, TabKey.USERS_PERMISSIONS]
    : [TabKey.INFO];

  const tabs: LinkItem[] = [
    {
      to: basePath,
      text: $t(tabMessages.info),
      isActive: (_match, location) => {
        const tab = new URLSearchParams(location.search).get('tab');
        return !tab || tab === TabKey.INFO;
      },
    },
  ];

  if (canEdit) {
    tabs.push(
      {
        to: `${basePath}?tab=${TabKey.GROUPS_PERMISSIONS}`,
        text: $t(tabMessages.groupsPermissions),
        isActive: (_match, location) =>
          new URLSearchParams(location.search).get('tab') === TabKey.GROUPS_PERMISSIONS,
      },
      {
        to: `${basePath}?tab=${TabKey.USERS_PERMISSIONS}`,
        text: $t(tabMessages.usersPermissions),
        isActive: (_match, location) =>
          new URLSearchParams(location.search).get('tab') === TabKey.USERS_PERMISSIONS,
      },
    );
  }

  const activeTab = availableTabs.includes(tabParam) ? (tabParam as TabKey) : TabKey.INFO;

  return { tabs, activeTab };
}
